import { useCallback } from "react";
import {
  ContentDeviceError,
  replaceContentDevice,
} from "../api/contentDeviceApi";
import { useDeviceStore } from "../views/tutorV2/store";
import { markDeviceChecked } from "../utils/contentDeviceCache";
import { DEVICE_LIMIT_MESSAGE, getDeviceManageUrl } from "../constants/device";

/**
 * 기기 등록 초과 안내 모달 프레젠테이션 훅
 *
 * 기획 STEP 01~04 흐름을 담당한다.
 * - select : 해제할 기기 선택
 * - confirm: 해제 확인 후 replace 호출 (해제 + 등록이 서버에서 원자적으로 처리)
 * - waiting: 통합마이페이지를 다녀온 경우의 재시도
 *
 * 교체가 성공하면 입력했던 메시지를 그대로 이어서 전송한다.
 */
export const useDeviceLimitModalView = () => {
  const {
    limitModalOpen,
    step,
    activeDevices,
    deviceLimit,
    selectedDeviceId,
    isProcessing,
    errorMessage,
    resumeSubmit,
    userId,
    conversationKey,
    closeLimitModal,
    selectDevice,
    moveToConfirmStep,
    backToSelectStep,
    moveToWaitingStep,
    setIsProcessing,
    setErrorMessage,
  } = useDeviceStore();

  const selectedDevice =
    activeDevices.find((device) => device.id === selectedDeviceId) || null;

  /** 전송을 재개한다. 판정 캐시를 갱신해 register 재호출을 피한다. */
  const resumePendingSubmit = useCallback(async () => {
    const onResume = resumeSubmit;

    // 교체 성공 = 이 기기가 등록된 상태이므로 판정을 통과한 것으로 기록
    markDeviceChecked(userId, conversationKey);
    closeLimitModal();

    await onResume?.();
  }, [resumeSubmit, userId, conversationKey, closeLimitModal]);

  // STEP 01 → 03: 선택한 기기의 해제 확인으로 이동
  const handleRequestRevoke = useCallback(() => {
    if (!selectedDeviceId) return;
    moveToConfirmStep();
  }, [selectedDeviceId, moveToConfirmStep]);

  // STEP 04: 교체 실행 (기존 해제 + 현재 기기 등록)
  const handleConfirmRevoke = useCallback(async () => {
    if (!selectedDeviceId) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      await replaceContentDevice(selectedDeviceId, "AIR");
      await resumePendingSubmit();
    } catch (error) {
      backToSelectStep();

      // 이미 해제된 기기라면 자리가 비었을 수 있으므로 재판정한다. (기획 Case 5 "목록 갱신")
      // 자리가 났으면 그대로 전송되고, 여전히 초과면 최신 목록으로 안내가 갱신된다.
      if (error instanceof ContentDeviceError && error.isDeviceNotFound) {
        await resumeSubmit?.();
        return;
      }

      // 그 외 실패는 등록 기기에 변화가 없다. 선택 화면에 오류만 노출한다.
      setErrorMessage(DEVICE_LIMIT_MESSAGE.REVOKE_FAILED);
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedDeviceId,
    resumePendingSubmit,
    resumeSubmit,
    backToSelectStep,
    setIsProcessing,
    setErrorMessage,
  ]);

  // 기기 관리 페이지를 새 탭으로 열고 재시도 대기 상태로 전환
  const handleMoveToDeviceManage = useCallback(() => {
    const url = getDeviceManageUrl();
    const opened = window.open(url, "_blank");

    // 팝업이 차단되면 현재 탭에서 이동시켜 안내가 끊기지 않도록 한다
    if (!opened) {
      window.location.assign(url);
      return;
    }

    moveToWaitingStep();
  }, [moveToWaitingStep]);

  // 기기 관리에서 해제하고 온 경우: 보류된 전송을 다시 실행해 재판정한다
  const handleRetry = useCallback(async () => {
    if (!resumeSubmit) {
      closeLimitModal();
      return;
    }

    setIsProcessing(true);

    try {
      // 모달을 열어둔 채 재판정한다.
      // 통과하면 useDeviceGuard가 닫아주고, 여전히 초과면
      // openLimitModal이 "재시도 실패"로 인식해 최신 목록과 문구를 갱신한다.
      await resumeSubmit();
    } finally {
      setIsProcessing(false);
    }
  }, [resumeSubmit, closeLimitModal, setIsProcessing]);

  // 취소: 해제 확인 단계에서는 선택 화면으로 복귀 (기획 Case 6), 그 외에는 닫기
  const handleCancel = useCallback(() => {
    if (step === "confirm") {
      backToSelectStep();
      return;
    }

    closeLimitModal();
  }, [step, backToSelectStep, closeLimitModal]);

  // X 버튼 / 오버레이 / ESC — 처리 중에는 닫지 않는다
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isProcessing) closeLimitModal();
    },
    [closeLimitModal, isProcessing]
  );

  // 서버가 기기 목록을 주지 않으면 선택할 대상이 없으므로 마이페이지로 유도한다
  const hasSelectableDevices = activeDevices.length > 0;

  const stepView = {
    select: {
      title: DEVICE_LIMIT_MESSAGE.SELECT_TITLE,
      description: hasSelectableDevices
        ? DEVICE_LIMIT_MESSAGE.getSelectDescription(deviceLimit)
        : DEVICE_LIMIT_MESSAGE.SELECT_EMPTY_DESCRIPTION,
      confirmText: hasSelectableDevices
        ? DEVICE_LIMIT_MESSAGE.SELECT_CONFIRM_BUTTON
        : DEVICE_LIMIT_MESSAGE.MOVE_BUTTON,
      onConfirm: hasSelectableDevices
        ? handleRequestRevoke
        : handleMoveToDeviceManage,
      confirmDisabled: hasSelectableDevices && !selectedDeviceId,
    },
    confirm: {
      title: DEVICE_LIMIT_MESSAGE.CONFIRM_TITLE,
      description: DEVICE_LIMIT_MESSAGE.getConfirmDescription(
        selectedDevice?.deviceName || ""
      ),
      confirmText: DEVICE_LIMIT_MESSAGE.REVOKE_BUTTON,
      onConfirm: handleConfirmRevoke,
      confirmDisabled: false,
    },
    waiting: {
      title: DEVICE_LIMIT_MESSAGE.WAITING_TITLE,
      description: DEVICE_LIMIT_MESSAGE.WAITING_DESCRIPTION,
      confirmText: DEVICE_LIMIT_MESSAGE.RETRY_BUTTON,
      onConfirm: handleRetry,
      confirmDisabled: false,
    },
  }[step];

  return {
    // 상태
    limitModalOpen,
    isSelectStep: step === "select",
    activeDevices,
    selectedDeviceId,
    isProcessing,
    errorMessage,

    // 문구
    title: stepView.title,
    description: stepView.description,
    confirmText: isProcessing
      ? DEVICE_LIMIT_MESSAGE.PROCESSING_BUTTON
      : stepView.confirmText,
    cancelText: DEVICE_LIMIT_MESSAGE.CANCEL_BUTTON,
    manageLinkText: DEVICE_LIMIT_MESSAGE.MANAGE_LINK,

    // 핸들러
    confirmDisabled: stepView.confirmDisabled || isProcessing,
    handleConfirm: stepView.onConfirm,
    handleCancel,
    handleSelectDevice: selectDevice,
    handleMoveToDeviceManage,
    handleOpenChange,
  };
};

export default useDeviceLimitModalView;
